import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';

@Component({
  standalone: true,
  selector: 'rv-quantity',
  templateUrl: './quantity.component.html',
  styleUrls: ['./quantity.component.scss'],
  imports: [ReactiveFormsModule, MatButtonModule, MatInputModule],
})
export class QuantityComponent
  implements ControlValueAccessor, OnChanges, OnInit
{
  @Input() min?: number;
  @Input() max?: number;
  @Input() required = false;
  @Input() step = 1;
  /**
   * When true, the value may only be changed via +/- buttons or arrow keys.
   * Values must be multiples of `step` (validated on the parent control).
   */
  @Input() enforceStep = false;
  /** Accessible label for the quantity control (spinbutton). */
  @Input() ariaLabel = 'Quantity';

  readonly inputFc = new FormControl('', { nonNullable: true });

  protected readonly destroyRef = inject(DestroyRef);

  /** When true, typed string could not be parsed as a finite number. */
  protected numericParseError = false;

  /** Value synced with the parent FormControl (number | null). */
  protected value: number | null = null;

  /** MatInput benefits from an id outside mat-form-field. */
  protected inputId = `rv-quantity-input-${QuantityComponent.nextId++}`;
  private static nextId = 0;

  private onChange: (v: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  /** Avoid feedback loop when syncing writeValue/disabled → inputFc. */
  private syncingInputFc = false;

  private readonly validatorFnBound = (control: AbstractControl) =>
    this.validateQuantity(control);

  constructor(
    private cdr: ChangeDetectorRef,
    @Self() @Optional() public ngControl: NgControl | null,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.inputFc.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((raw) => {
        if (!this.syncingInputFc) {
          this.processUserText(raw ?? '');
        }
      });
  }

  ngOnInit(): void {
    const ctrl = this.ngControl?.control;
    if (!ctrl) {
      return;
    }
    ctrl.addValidators(this.validatorFnBound);
    ctrl.updateValueAndValidity({ emitEvent: false });

    this.destroyRef.onDestroy(() => {
      ctrl.removeValidators(this.validatorFnBound);
      ctrl.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.min ||
      changes.max ||
      changes.required ||
      changes.step ||
      changes.enforceStep
    ) {
      this.scheduleValidatorRefresh();
    }
  }

  private validateQuantity(control: AbstractControl): ValidationErrors | null {
    const parentVal = control.value;

    if (this.numericParseError) {
      return { number: true };
    }

    if (
      this.required &&
      (parentVal === null || parentVal === undefined || parentVal === '')
    ) {
      return { required: true };
    }

    if (parentVal === null || parentVal === undefined || parentVal === '') {
      return null;
    }

    const num = typeof parentVal === 'number' ? parentVal : Number(parentVal);
    if (!Number.isFinite(num)) {
      return { number: true };
    }

    if (this.min !== undefined && num < this.min) {
      return { min: { min: this.min, actual: num } };
    }
    if (this.max !== undefined && num > this.max) {
      return { max: { max: this.max, actual: num } };
    }
    if (
      this.enforceStep &&
      Number.isFinite(this.step) &&
      this.step > 0 &&
      num % this.step !== 0
    ) {
      return { multipleOf: { step: this.step, actual: num } };
    }
    return null;
  }

  writeValue(obj: number | string | null | undefined): void {
    if (obj === null || obj === undefined || obj === '') {
      this.value = null;
      this.numericParseError = false;
      this.setInputFcValue('');
    } else {
      const num = typeof obj === 'number' ? obj : Number(obj);
      if (!Number.isFinite(num)) {
        this.value = null;
        this.numericParseError = false;
        this.setInputFcValue('');
      } else {
        this.value = num;
        this.numericParseError = false;
        this.setInputFcValue(this.formatDisplay(num));
      }
    }
    this.scheduleValidatorRefresh();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.inputFc.disable({ emitEvent: false });
    } else {
      this.inputFc.enable({ emitEvent: false });
    }
    this.cdr.markForCheck();
  }

  protected shouldShowErrors(): boolean {
    const ctrl = this.ngControl?.control;
    if (!ctrl?.errors) return false;
    return ctrl.disabled ? false : ctrl.touched || ctrl.dirty;
  }

  protected getPrimaryError(): string | null {
    const ctrl = this.ngControl?.control;
    const err = ctrl?.errors;
    if (!err || !this.shouldShowErrors()) return null;
    if (err['required']) return 'This field is required.';
    if (err['number']) return 'Please enter a valid number.';
    if (err['min']) {
      const m = err['min']?.min;
      return typeof m === 'number'
        ? `Minimum value is ${m}.`
        : 'Value is too low.';
    }
    if (err['max']) {
      const m = err['max']?.max;
      return typeof m === 'number'
        ? `Maximum value is ${m}.`
        : 'Value is too high.';
    }
    if (err['multipleOf']) {
      const s = err['multipleOf']?.step;
      return typeof s === 'number'
        ? `Value must be a multiple of ${s}.`
        : 'Value must match the allowed step.';
    }
    return null;
  }

  protected onBlur(): void {
    if (this.inputFc.disabled) return;
    if (
      !this.numericParseError &&
      this.value !== null &&
      this.value !== undefined
    ) {
      const formatted = this.formatDisplay(this.value);
      this.setInputFcValue(formatted);
    }
    this.markTouched();
  }

  protected decrement(): void {
    if (this.inputFc.disabled || this.decrementDisabled()) return;
    const current = this.value ?? 0;
    const next = current - this.step;
    const clamped = this.clampToBounds(next);
    this.numericParseError = false;
    this.value = clamped;
    this.setInputFcValue(this.formatDisplay(clamped));
    this.emitValue(clamped);
    this.scheduleValidatorRefresh();
    this.markTouched();
  }

  protected increment(): void {
    if (this.inputFc.disabled || this.incrementDisabled()) return;
    const current = this.value ?? 0;
    const next = current + this.step;
    const clamped = this.clampToBounds(next);
    this.numericParseError = false;
    this.value = clamped;
    this.setInputFcValue(this.formatDisplay(clamped));
    this.emitValue(clamped);
    this.scheduleValidatorRefresh();
    this.markTouched();
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (this.inputFc.disabled) return;
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.increment();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.decrement();
    } else if (this.enforceStep) {
      this.blockTextEditKey(event);
    }
  }

  protected onPaste(event: ClipboardEvent): void {
    if (this.enforceStep) {
      event.preventDefault();
    }
  }

  protected decrementDisabled(): boolean {
    if (this.inputFc.disabled) return true;
    if (!Number.isFinite(this.step)) return true;
    if (this.min === undefined) return false;
    const current = this.value ?? 0;
    return current - this.step < this.min;
  }

  protected incrementDisabled(): boolean {
    if (this.inputFc.disabled) return true;
    if (!Number.isFinite(this.step)) return true;
    if (this.max === undefined) return false;
    const current = this.value ?? 0;
    return current + this.step > this.max;
  }

  protected get disabled(): boolean {
    return !!this.inputFc.disabled;
  }

  protected get ariaValueNow(): string | undefined {
    if (
      this.numericParseError ||
      this.value === null ||
      this.value === undefined
    ) {
      return undefined;
    }
    return String(this.value);
  }

  protected get ariaMin(): string | undefined {
    return this.min !== undefined ? String(this.min) : undefined;
  }

  protected get ariaMax(): string | undefined {
    return this.max !== undefined ? String(this.max) : undefined;
  }

  private processUserText(raw: string): void {
    if (this.inputFc.disabled) return;
    if (this.enforceStep) {
      this.revertInputToValue();
      return;
    }

    const trimmed = raw.trim();
    if (trimmed === '') {
      this.numericParseError = false;
      this.value = null;
      this.emitValue(null);
      this.scheduleValidatorRefresh();
      return;
    }

    const n = Number(trimmed.replace(',', '.'));
    if (!Number.isFinite(n)) {
      this.numericParseError = true;
      this.value = null;
      this.emitValue(null);
      this.scheduleValidatorRefresh();
      return;
    }

    this.numericParseError = false;
    this.value = n;
    this.emitValue(n);
    this.scheduleValidatorRefresh();
  }

  private clampToBounds(n: number): number {
    let x = n;
    if (this.min !== undefined) x = Math.max(this.min, x);
    if (this.max !== undefined) x = Math.min(this.max, x);
    return x;
  }

  private formatDisplay(n: number): string {
    if (Number.isInteger(n)) return String(Math.trunc(n));
    return String(n);
  }

  private revertInputToValue(): void {
    const display =
      this.value !== null && this.value !== undefined
        ? this.formatDisplay(this.value)
        : '';
    if (this.inputFc.value !== display) {
      this.setInputFcValue(display);
    }
  }

  private blockTextEditKey(event: KeyboardEvent): void {
    const navigationKeys = new Set([
      'Tab',
      'Escape',
      'Enter',
      'Home',
      'End',
      'Shift',
      'Control',
      'Alt',
      'Meta',
    ]);
    if (navigationKeys.has(event.key)) {
      return;
    }
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c'].includes(event.key.toLowerCase())
    ) {
      return;
    }
    event.preventDefault();
  }

  private setInputFcValue(v: string): void {
    this.syncingInputFc = true;
    try {
      this.inputFc.setValue(v, { emitEvent: false });
    } finally {
      this.syncingInputFc = false;
    }
  }

  private emitValue(v: number | null): void {
    this.onChange(v);
    this.ngControl?.control?.markAsDirty({ onlySelf: true });
  }

  private markTouched(): void {
    this.onTouched();
    this.ngControl?.control?.markAsTouched({ onlySelf: true });
  }

  private scheduleValidatorRefresh(): void {
    queueMicrotask(() => {
      const c = this.ngControl?.control;
      if (c) {
        c.updateValueAndValidity({ emitEvent: false });
      }
      this.cdr.markForCheck();
    });
  }
}
